import { Fragment, Suspense, lazy, useEffect, type PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { heartbeat, initializeAccount, signOut, useAccountSession } from './accountSession';
import { accountReturnPath } from './accountReturn';
import { scopedStorage } from './standaloneStorage';
import { applyRemoteUserData, retryUserDataSync, startUserDataSync, useUserDataSync } from './userDataSync';
import SyncFeedback from './SyncFeedback';

const LoginPage = lazy(() => import('@/pages/LoginPage/LoginPage'));
const tabs = new Set(['/', '/training', '/diet', '/custom', '/profile']);
const loading = <div className="app-bg min-h-screen flex items-center justify-center" role="status">正在加载练刻…</div>;

export default function AccountBootstrap({ children }: PropsWithChildren) {
  const { ready, user, version } = useAccountSession();
  const sync = useUserDataSync();
  const location = useLocation();
  useEffect(() => { void initializeAccount(); }, []);
  useEffect(() => {
    if (!user) return;
    return startUserDataSync(user.id);
  }, [user?.id, version]);
  useEffect(() => {
    if (!user) return;
    const update = () => { void heartbeat(document.visibilityState === 'visible').catch(() => {}); };
    update();
    const interval = window.setInterval(update, 30000);
    document.addEventListener('visibilitychange', update);
    return () => { window.clearInterval(interval); document.removeEventListener('visibilitychange', update); };
  }, [user?.id, version]);
  useEffect(() => {
    if (!user || !sync.remote) return;
    // Refresh idle main screens without interrupting forms or workout timers.
    const interval = window.setInterval(() => {
      if (!tabs.has(location.pathname) || document.visibilityState !== 'visible') return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (document.activeElement?.matches('input,textarea,select,[contenteditable="true"]')) return;
      try {
        const workout = JSON.parse(scopedStorage.getItem(`u_${user.id}:active_workout`) || 'null');
        if (workout?.active) return;
      } catch { /* Invalid sessions do not lock cloud updates. */ }
      applyRemoteUserData();
    }, 1500);
    return () => window.clearInterval(interval);
  }, [user?.id, sync.remote, location.pathname]);
  if (!ready) return loading;
  if (!user) {
    if (location.pathname === '/account') return <Navigate to={'/login' + location.search} replace />;
    if (location.pathname !== '/login') {
      const next = accountReturnPath(location.pathname + location.search + location.hash);
      return <Navigate to={'/login?next=' + encodeURIComponent(next)} replace />;
    }
    return <Suspense fallback={loading}><LoginPage /></Suspense>;
  }
  if (sync.accountId !== user.id || sync.phase === 'loading') return <div className="app-bg min-h-screen flex items-center justify-center text-sm" role="status">正在恢复账号数据…</div>;
  if (sync.phase === 'error') return <div className="app-bg min-h-screen flex items-center justify-center px-6">
    <div className="metallic-card rounded-2xl p-6 w-full max-w-[380px] text-center">
      <h1 className="text-lg font-semibold">暂时无法恢复账号数据</h1>
      <p className="text-sm text-muted-foreground my-4" role="alert">{sync.error}</p>
      <button className="btn-primary-gradient rounded-xl px-6 py-3 text-white text-sm" onClick={retryUserDataSync}>重新连接</button>
      <button className="block mx-auto mt-4 text-xs text-muted-foreground" onClick={() => { void signOut().catch(() => {}); }}>退出账号</button>
    </div>
  </div>;
  if (location.pathname === '/login' || location.pathname === '/account') {
    return <Navigate to={accountReturnPath(new URLSearchParams(location.search).get('next'))} replace />;
  }
  return <Fragment key={user.id + ':' + sync.epoch}>{children}<SyncFeedback /></Fragment>;
}
